
import { useState, useEffect, useRef } from "react";
import { isLowDataMode } from "@/utils/networkMonitor";
import { clearServiceCaches } from "@/utils/cacheManager";
import { useQueryClient } from "@tanstack/react-query";

interface NetworkStatus {
  effectiveType: string;
  downlink?: number;
}

/**
 * Hook to provide network awareness for queries
 */
export const useNetworkAwareQuery = () => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({ 
    effectiveType: 'unknown'
  });
  const queryClient = useQueryClient();
  const [hasToasted, setHasToasted] = useState<boolean>(false);
  const [hasRecoveredFromError, setHasRecoveredFromError] = useState<boolean>(false);
  
  const isMobile = useRef<boolean>(
    typeof window !== 'undefined' && 
    (window.innerWidth < 768 || 
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
  ).current;
  
  const isLowData = useRef<boolean>(isLowDataMode()).current;

  useEffect(() => {
    function updateOnlineStatus() {
      const onlineStatus = navigator.onLine;
      setIsOnline(onlineStatus);
      
      if (onlineStatus) {
        // We're back online, clear caches to get fresh data
        clearServiceCaches(queryClient).then(() => {
          console.log('[Network] Back online, cache cleared');
        });
      }
    }

    function updateConnectionStatus() {
      if ('connection' in navigator) {
        // @ts-ignore - Connection API may not be recognized by TypeScript
        const connection = navigator.connection || 
          // @ts-ignore
          navigator.mozConnection || 
          // @ts-ignore
          navigator.webkitConnection || 
          { effectiveType: 'unknown', downlink: undefined };

        setNetworkStatus({
          effectiveType: connection.effectiveType || 'unknown',
          downlink: connection.downlink
        });
      }
    }

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    
    if ('connection' in navigator) {
      // @ts-ignore - connectionChange event not recognized by TypeScript
      navigator.connection?.addEventListener('change', updateConnectionStatus);
    }
    
    // Initial connection check
    updateOnlineStatus();
    updateConnectionStatus();

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      
      if ('connection' in navigator) {
        // @ts-ignore - connectionChange event not recognized by TypeScript
        navigator.connection?.removeEventListener('change', updateConnectionStatus);
      }
    };
  }, [queryClient]);

  return {
    isOnline,
    networkStatus,
    hasToasted,
    setHasToasted,
    isMobile,
    isLowData,
    hasRecoveredFromError,
    setHasRecoveredFromError
  };
};
